import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
  useToast,
} from '@caruso/ui';
import { listsService, CreateListDto, UpdateListDto, ListMember } from '@/services/lists.service';
import { companiesService } from '@/services/companies.service';
import { positionsService } from '@/services/positions.service';
import { tagsService } from '@/services/tags.service';
import { clientsService } from '@/services/clients.service';
import {
  ArrowLeft,
  Save,
  Filter as FilterIcon,
  Users,
  Plus,
  X,
  Search,
  Loader2,
} from 'lucide-react';
import { cn } from '@caruso/ui';

const listSchema = z.object({
  name: z.string().min(1, 'Nome é obrigatório'),
  description: z.string().optional(),
  active: z.boolean().default(true),
});

type ListFormData = z.infer<typeof listSchema>;

export function ListFormPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const isEdit = !!id;

  const [activeTab, setActiveTab] = useState<'info' | 'filters' | 'members'>('info');
  const [selectedCompanyIds, setSelectedCompanyIds] = useState<string[]>([]);
  const [selectedPositionIds, setSelectedPositionIds] = useState<string[]>([]);
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);
  const [searchClient, setSearchClient] = useState('');
  const [filteredClients, setFilteredClients] = useState<any[]>([]);
  const [showClientDropdown, setShowClientDropdown] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
    reset,
  } = useForm<ListFormData>({
    resolver: zodResolver(listSchema),
    defaultValues: {
      active: true,
    },
  });

  const {
    data: list,
    isLoading: isLoadingList,
    refetch: refetchList,
  } = useQuery({
    queryKey: ['list', id],
    queryFn: () => listsService.getById(id!),
    enabled: isEdit,
  });

  const { data: companiesData } = useQuery({
    queryKey: ['companies-all'],
    queryFn: () => companiesService.getAll({ page: 1, pageSize: 100 }),
  });

  const { data: positionsData } = useQuery({
    queryKey: ['positions-corporate'],
    queryFn: () => positionsService.getAll({ page: 1, pageSize: 100, type: 'corporate' }),
  });

  const { data: tagsData } = useQuery({
    queryKey: ['tags-all'],
    queryFn: () => tagsService.getAll(),
  });

  const { data: clientsData } = useQuery({
    queryKey: ['clients-all'],
    queryFn: () => clientsService.getAll({ page: 1, pageSize: 100 }),
  });

  useEffect(() => {
    if (list) {
      reset({
        name: list.name,
        description: list.description || '',
        active: list.active,
      });

      if (list.filters) {
        setSelectedCompanyIds(list.filters.companyIds || []);
        setSelectedPositionIds(list.filters.positionIds || []);
        setSelectedTagIds(list.filters.tagIds || []);
      }
    }
  }, [list, reset]);

  useEffect(() => {
    if (searchClient && clientsData?.data) {
      const filtered = clientsData.data.filter(
        (client) =>
          client.name.toLowerCase().includes(searchClient.toLowerCase()) ||
          client.email?.toLowerCase().includes(searchClient.toLowerCase())
      );
      setFilteredClients(filtered);
      setShowClientDropdown(filtered.length > 0);
    } else {
      setFilteredClients([]);
      setShowClientDropdown(false);
    }
  }, [searchClient, clientsData]);

  const saveMutation = useMutation({
    mutationFn: async (data: ListFormData) => {
      const payload: CreateListDto | UpdateListDto = {
        ...data,
      };

      // Só incluir filters se houver pelo menos um filtro selecionado
      if (selectedCompanyIds.length > 0 || selectedPositionIds.length > 0 || selectedTagIds.length > 0) {
        payload.filters = {
          companyIds: selectedCompanyIds.length > 0 ? selectedCompanyIds : undefined,
          positionIds: selectedPositionIds.length > 0 ? selectedPositionIds : undefined,
          tagIds: selectedTagIds.length > 0 ? selectedTagIds : undefined,
        };
      }

      if (isEdit) {
        return listsService.update(id!, payload);
      }
      return listsService.create(payload as CreateListDto);
    },
    onSuccess: () => {
      // Invalida o cache para atualizar a listagem
      queryClient.invalidateQueries({ queryKey: ['lists'] });

      toast({
        title: isEdit ? 'Lista atualizada' : 'Lista criada',
        description: isEdit
          ? 'A lista foi atualizada com sucesso'
          : 'A lista foi criada com sucesso',
        variant: 'success',
      });
      navigate('/app/listas');
    },
    onError: (error: any) => {
      toast({
        title: 'Erro ao salvar lista',
        description: error.response?.data?.message || 'Ocorreu um erro ao salvar a lista',
        variant: 'destructive',
      });
    },
  });

  const applyFiltersMutation = useMutation({
    mutationFn: async () => {
      if (!id) return;
      const filters: any = {};
      if (selectedCompanyIds.length > 0) filters.companyIds = selectedCompanyIds;
      if (selectedPositionIds.length > 0) filters.positionIds = selectedPositionIds;
      if (selectedTagIds.length > 0) filters.tagIds = selectedTagIds;
      return listsService.applyFilters(id, filters);
    },
    onSuccess: () => {
      // Invalida o cache para atualizar a listagem
      queryClient.invalidateQueries({ queryKey: ['lists'] });

      toast({
        title: 'Filtros aplicados',
        description: 'Os filtros foram aplicados com sucesso',
        variant: 'success',
      });
      refetchList();
    },
    onError: (error: any) => {
      toast({
        title: 'Erro ao aplicar filtros',
        description: error.response?.data?.message || 'Ocorreu um erro ao aplicar os filtros',
        variant: 'destructive',
      });
    },
  });

  const addMemberMutation = useMutation({
    mutationFn: async (clientId: string) => {
      if (!id) return;
      return listsService.addMember(id, clientId);
    },
    onSuccess: () => {
      // Invalida o cache para atualizar a listagem
      queryClient.invalidateQueries({ queryKey: ['lists'] });

      toast({
        title: 'Membro adicionado',
        description: 'O membro foi adicionado com sucesso',
        variant: 'success',
      });
      setSearchClient('');
      refetchList();
    },
    onError: (error: any) => {
      toast({
        title: 'Erro ao adicionar membro',
        description: error.response?.data?.message || 'Ocorreu um erro ao adicionar o membro',
        variant: 'destructive',
      });
    },
  });

  const removeMemberMutation = useMutation({
    mutationFn: async (memberId: string) => {
      if (!id) return;
      return listsService.removeMember(id, memberId);
    },
    onSuccess: () => {
      // Invalida o cache para atualizar a listagem
      queryClient.invalidateQueries({ queryKey: ['lists'] });

      toast({
        title: 'Membro removido',
        description: 'O membro foi removido com sucesso',
        variant: 'success',
      });
      refetchList();
    },
    onError: (error: any) => {
      toast({
        title: 'Erro ao remover membro',
        description: error.response?.data?.message || 'Ocorreu um erro ao remover o membro',
        variant: 'destructive',
      });
    },
  });

  const onSubmit = (data: ListFormData) => {
    saveMutation.mutate(data);
  };

  const handleToggleCompany = (companyId: string) => {
    setSelectedCompanyIds((prev) =>
      prev.includes(companyId) ? prev.filter((id) => id !== companyId) : [...prev, companyId]
    );
  };

  const handleTogglePosition = (positionId: string) => {
    setSelectedPositionIds((prev) =>
      prev.includes(positionId) ? prev.filter((id) => id !== positionId) : [...prev, positionId]
    );
  };

  const handleToggleTag = (tagId: string) => {
    setSelectedTagIds((prev) =>
      prev.includes(tagId) ? prev.filter((id) => id !== tagId) : [...prev, tagId]
    );
  };

  const handleSelectClient = (client: any) => {
    addMemberMutation.mutate(client.id);
    setShowClientDropdown(false);
  };

  if (isLoadingList) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-brand-gold" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/app/listas')}>
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h1 className="text-3xl font-serif text-brand-gold">
              {isEdit ? 'Editar Lista' : 'Nova Lista'}
            </h1>
            <p className="text-brand-ivory/60 mt-1">
              {isEdit ? 'Atualize as informações da lista' : 'Preencha os dados da nova lista'}
            </p>
          </div>
        </div>
        <Button
          onClick={handleSubmit(onSubmit)}
          variant="premium"
          disabled={saveMutation.isPending}
        >
          {saveMutation.isPending ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Save className="w-4 h-4 mr-2" />
          )}
          Salvar
        </Button>
      </div>

      <Card className="bg-brand-ink/50 border-brand-gold/20">
        <div className="flex border-b border-brand-gold/20">
          <button
            className={cn(
              'px-6 py-3 text-sm font-medium transition-colors',
              activeTab === 'info'
                ? 'text-brand-gold border-b-2 border-brand-gold'
                : 'text-brand-ivory/60 hover:text-brand-ivory'
            )}
            onClick={() => setActiveTab('info')}
          >
            Informações
          </button>
          <button
            className={cn(
              'px-6 py-3 text-sm font-medium transition-colors',
              activeTab === 'filters'
                ? 'text-brand-gold border-b-2 border-brand-gold'
                : 'text-brand-ivory/60 hover:text-brand-ivory'
            )}
            onClick={() => setActiveTab('filters')}
          >
            <FilterIcon className="w-4 h-4 inline mr-2" />
            Filtros
          </button>
          {isEdit && (
            <button
              className={cn(
                'px-6 py-3 text-sm font-medium transition-colors',
                activeTab === 'members'
                  ? 'text-brand-gold border-b-2 border-brand-gold'
                  : 'text-brand-ivory/60 hover:text-brand-ivory'
              )}
              onClick={() => setActiveTab('members')}
            >
              <Users className="w-4 h-4 inline mr-2" />
              Membros ({list?.members?.length || 0})
            </button>
          )}
        </div>
      </Card>

      {activeTab === 'info' && (
        <Card className="bg-brand-ink/50 border-brand-gold/20">
          <CardHeader>
            <CardTitle className="text-brand-gold">Informações Básicas</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-brand-ivory">
                Nome *
              </Label>
              <Input
                id="name"
                {...register('name')}
                className="bg-brand-smoke/50 border-brand-gold/20 text-brand-ivory"
                placeholder="Nome da lista"
              />
              {errors.name && (
                <p className="text-sm text-red-400">{errors.name.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="description" className="text-brand-ivory">
                Descrição
              </Label>
              <textarea
                id="description"
                {...register('description')}
                className="w-full rounded-md bg-brand-smoke/50 border border-brand-gold/20 text-brand-ivory px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-gold/50"
                placeholder="Descrição da lista"
                rows={4}
              />
            </div>

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="active"
                checked={watch('active')}
                onChange={(e) => setValue('active', e.target.checked)}
                className="h-4 w-4 rounded border-brand-gold/20 bg-brand-smoke/50 text-brand-gold focus:ring-brand-gold/50"
              />
              <Label htmlFor="active" className="text-brand-ivory cursor-pointer">
                Lista ativa
              </Label>
            </div>
          </CardContent>
        </Card>
      )}

      {activeTab === 'filters' && (
        <div className="space-y-6">
          <Card className="bg-brand-ink/50 border-brand-gold/20">
            <CardHeader>
              <CardTitle className="text-brand-gold">Filtrar por Empresas</CardTitle>
              <p className="text-sm text-brand-ivory/60 mt-1">
                Selecione as empresas para filtrar clientes
              </p>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {companiesData?.data.map((company) => (
                  <div
                    key={company.id}
                    className={cn(
                      'flex items-center space-x-3 p-3 rounded-lg border transition-colors cursor-pointer',
                      selectedCompanyIds.includes(company.id)
                        ? 'bg-brand-gold/10 border-brand-gold'
                        : 'bg-brand-smoke/30 border-brand-gold/20 hover:border-brand-gold/40'
                    )}
                    onClick={() => handleToggleCompany(company.id)}
                  >
                    <input
                      type="checkbox"
                      checked={selectedCompanyIds.includes(company.id)}
                      onChange={(e) => e.stopPropagation()}
                      onClick={(e) => e.stopPropagation()}
                      className="h-4 w-4 rounded border-brand-gold/20 bg-brand-smoke/50 text-brand-gold pointer-events-none"
                    />
                    <span className="text-sm text-brand-ivory">{company.name}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card className="bg-brand-ink/50 border-brand-gold/20">
            <CardHeader>
              <CardTitle className="text-brand-gold">Filtrar por Cargos</CardTitle>
              <p className="text-sm text-brand-ivory/60 mt-1">
                Selecione os cargos para filtrar clientes
              </p>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {positionsData?.data.map((position) => (
                  <div
                    key={position.id}
                    className={cn(
                      'flex items-center space-x-3 p-3 rounded-lg border transition-colors cursor-pointer',
                      selectedPositionIds.includes(position.id)
                        ? 'bg-brand-gold/10 border-brand-gold'
                        : 'bg-brand-smoke/30 border-brand-gold/20 hover:border-brand-gold/40'
                    )}
                    onClick={() => handleTogglePosition(position.id)}
                  >
                    <input
                      type="checkbox"
                      checked={selectedPositionIds.includes(position.id)}
                      onChange={(e) => e.stopPropagation()}
                      onClick={(e) => e.stopPropagation()}
                      className="h-4 w-4 rounded border-brand-gold/20 bg-brand-smoke/50 text-brand-gold pointer-events-none"
                    />
                    <span className="text-sm text-brand-ivory">{position.name}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card className="bg-brand-ink/50 border-brand-gold/20">
            <CardHeader>
              <CardTitle className="text-brand-gold">Filtrar por Tags</CardTitle>
              <p className="text-sm text-brand-ivory/60 mt-1">
                Selecione as tags para filtrar clientes
              </p>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {tagsData?.map((tag) => (
                  <div
                    key={tag.id}
                    className={cn(
                      'flex items-center space-x-3 p-3 rounded-lg border transition-colors cursor-pointer',
                      selectedTagIds.includes(tag.id)
                        ? 'bg-brand-gold/10 border-brand-gold'
                        : 'bg-brand-smoke/30 border-brand-gold/20 hover:border-brand-gold/40'
                    )}
                    onClick={() => handleToggleTag(tag.id)}
                  >
                    <input
                      type="checkbox"
                      checked={selectedTagIds.includes(tag.id)}
                      onChange={(e) => e.stopPropagation()}
                      onClick={(e) => e.stopPropagation()}
                      className="h-4 w-4 rounded border-brand-gold/20 bg-brand-smoke/50 text-brand-gold pointer-events-none"
                    />
                    <span className="text-sm text-brand-ivory">{tag.name}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {isEdit && (
            <div className="flex justify-end">
              <Button
                onClick={() => applyFiltersMutation.mutate()}
                variant="premium"
                disabled={applyFiltersMutation.isPending}
              >
                {applyFiltersMutation.isPending ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <FilterIcon className="w-4 h-4 mr-2" />
                )}
                Aplicar Filtros
              </Button>
            </div>
          )}
        </div>
      )}

      {activeTab === 'members' && isEdit && (
        <div className="space-y-6">
          <Card className="bg-brand-ink/50 border-brand-gold/20">
            <CardHeader>
              <CardTitle className="text-brand-gold">Adicionar Membro</CardTitle>
              <p className="text-sm text-brand-ivory/60 mt-1">
                Busque e adicione clientes individualmente à lista
              </p>
            </CardHeader>
            <CardContent>
              <div className="relative">
                {addMemberMutation.isPending ? (
                  <Loader2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-gold animate-spin" />
                ) : (
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-gold/50" />
                )}
                <Input
                  placeholder="Buscar cliente por nome ou email..."
                  value={searchClient}
                  onChange={(e) => setSearchClient(e.target.value)}
                  className="pl-10 bg-brand-smoke/50 border-brand-gold/20 text-brand-ivory"
                  disabled={addMemberMutation.isPending}
                />
                {showClientDropdown && !addMemberMutation.isPending && (
                  <div className="absolute z-10 w-full mt-2 bg-brand-ink border border-brand-gold/20 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {filteredClients.map((client) => (
                      <div
                        key={client.id}
                        className="p-3 hover:bg-brand-gold/10 cursor-pointer border-b border-brand-gold/10 last:border-0"
                        onClick={() => handleSelectClient(client)}
                      >
                        <p className="text-brand-ivory font-medium">{client.name}</p>
                        {client.email && (
                          <p className="text-sm text-brand-ivory/60">{client.email}</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                {addMemberMutation.isPending && (
                  <div className="mt-2 p-3 bg-brand-gold/10 border border-brand-gold/20 rounded-lg text-center">
                    <p className="text-sm text-brand-gold">Adicionando membro...</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          <Card className="bg-brand-ink/50 border-brand-gold/20">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-brand-gold">
                    Membros da Lista ({list?.members?.length || 0})
                  </CardTitle>
                  <p className="text-sm text-brand-ivory/60 mt-1">
                    Membros adicionados manualmente serão mantidos ao atualizar
                  </p>
                </div>
                {(selectedCompanyIds.length > 0 || selectedPositionIds.length > 0 || selectedTagIds.length > 0) && (
                  <Button
                    onClick={() => applyFiltersMutation.mutate()}
                    variant="outline"
                    disabled={applyFiltersMutation.isPending}
                  >
                    {applyFiltersMutation.isPending ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <FilterIcon className="w-4 h-4 mr-2" />
                    )}
                    Atualizar Filtros
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {list?.members && list.members.length > 0 ? (
                <div className="space-y-2">
                  {list.members.map((member: ListMember) => (
                    <div
                      key={member.id}
                      className="flex items-center justify-between p-3 bg-brand-smoke/30 rounded-lg border border-brand-gold/20"
                    >
                      <div className="flex-1">
                        <p className="text-brand-ivory font-medium">{member.client.name}</p>
                        <div className="flex gap-3 mt-1 text-sm text-brand-ivory/60">
                          {member.client.email && <span>{member.client.email}</span>}
                          {member.client.phone && <span>{member.client.phone}</span>}
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span
                          className={cn(
                            'px-2 py-1 text-xs rounded-full',
                            member.addedType === 'filter'
                              ? 'bg-blue-500/20 text-blue-400'
                              : 'bg-green-500/20 text-green-400'
                          )}
                        >
                          {member.addedType === 'filter' ? 'Por filtro' : 'Manual'}
                        </span>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => removeMemberMutation.mutate(member.id)}
                          disabled={removeMemberMutation.isPending}
                        >
                          {removeMemberMutation.isPending ? (
                            <Loader2 className="w-4 h-4 text-brand-gold animate-spin" />
                          ) : (
                            <X className="w-4 h-4 text-red-400" />
                          )}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-brand-ivory/50">
                  <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>Nenhum membro na lista</p>
                  <p className="text-sm mt-1">
                    Adicione membros manualmente ou aplique filtros
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
